import React from 'react'
import { Button, Icon, Message } from 'semantic-ui-react'
import styled, { css } from 'styled-components'

/** Surface colour from the app theme (works inside portals too, unlike a CSS variable on the page). */
export const surface = ({ theme }) => theme?.crm?.surface || '#ffffff'
/** Text colour from the app theme. */
export const textColor = ({ theme }) => theme?.crm?.text || 'inherit'

/**
 * Dark-mode overrides for semantic-ui controls inside the CRM (tables, inputs, buttons, messages).
 * Applied by the CRM page and by its portaled drawers/dialogs; does nothing in light mode.
 */
export const darkControls = css`
    ${({ theme }) =>
        theme?.crm?.mode === 'dark' &&
        css`
            && .ui.message {
                background: ${theme.crm.surface};
                color: ${theme.crm.text};
                box-shadow: 0 0 0 1px rgba(128, 128, 128, 0.4) inset;
            }
            && .ui.message .header,
            && h1,
            && h2,
            && h3,
            && h4 {
                color: ${theme.crm.text};
            }
            && .ui.table,
            && .ui.celled.table {
                background: ${theme.crm.surface};
                color: ${theme.crm.text};
                border-color: rgba(128, 128, 128, 0.35);
            }
            && .ui.table thead th,
            && .ui.sortable.table thead th,
            && .ui.celled.table thead th {
                background: rgba(255, 255, 255, 0.08) !important;
                color: ${theme.crm.text} !important;
                border-color: rgba(128, 128, 128, 0.35) !important;
            }
            && .ui.sortable.table thead th:hover,
            && .ui.sortable.table thead th.sorted {
                background: rgba(255, 255, 255, 0.14) !important;
                color: ${theme.crm.text} !important;
            }
            && .ui.table tr td,
            && .ui.celled.table tr td,
            && .ui.celled.table tr th {
                border-color: rgba(128, 128, 128, 0.3);
            }
            && .ui.selectable.table tbody tr:hover {
                background: rgba(255, 255, 255, 0.06) !important;
                color: ${theme.crm.text} !important;
            }
            && input,
            && select,
            && textarea,
            && .ui.input > input,
            && .ui.form input:not([type]),
            && .ui.form input[type='text'],
            && .ui.form input[type='email'],
            && .ui.form input[type='tel'],
            && .ui.form input[type='number'],
            && .ui.form input[type='date'],
            && .ui.form textarea,
            && .ui.selection.dropdown {
                background: ${theme.crm.input};
                color: ${theme.crm.text};
                border-color: rgba(128, 128, 128, 0.5);
            }
            && .ui.dropdown .menu,
            && .ui.selection.dropdown .menu {
                background: ${theme.crm.surface};
                border-color: rgba(128, 128, 128, 0.4);
            }
            && .ui.dropdown .menu > .item {
                color: ${theme.crm.text};
            }
            && .ui.dropdown .menu > .item:hover,
            && .ui.dropdown .menu .selected.item {
                background: rgba(255, 255, 255, 0.08);
            }
            && .ui.form .field > label,
            && .ui.checkbox label,
            && .ui.checkbox label:hover {
                color: ${theme.crm.text};
            }
            && .ui.button:not(.primary):not(.negative):not(.red) {
                background: rgba(255, 255, 255, 0.1);
                color: ${theme.crm.text};
            }
            && .ui.basic.button {
                background: transparent;
                color: ${theme.crm.text} !important;
                box-shadow: 0 0 0 1px rgba(128, 128, 128, 0.55) inset !important;
            }
            && .ui.pagination.menu,
            && .ui.pagination.menu .item {
                background: ${theme.crm.surface};
                color: ${theme.crm.text};
            }
            && .ui.pagination.menu .active.item {
                background: rgba(255, 255, 255, 0.12);
            }
            && a {
                color: #93c5fd;
            }
        `}
`

// Colours use translucent tints so they read on both light and dark surfaces.
export const Row = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ $gap = 8 }) => $gap}px;
    flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
    justify-content: ${({ $between }) => ($between ? 'space-between' : 'flex-start')};
`

export const Muted = styled.span`
    opacity: 0.7;
    font-size: ${({ $size }) => $size || '0.85rem'};
`

export const Card = styled.div`
    border: 1px solid rgba(128, 128, 128, 0.3);
    border-radius: 10px;
    padding: 14px 16px;
    background: ${surface};
    color: ${textColor};
`

const TONES = {
    blue: ['rgba(59,130,246,.18)', '#2563eb', '#93c5fd'],
    green: ['rgba(34,197,94,.18)', '#15803d', '#86efac'],
    amber: ['rgba(245,158,11,.2)', '#b45309', '#fcd34d'],
    red: ['rgba(239,68,68,.18)', '#b91c1c', '#fca5a5'],
    gray: ['rgba(128,128,128,.2)', 'inherit', 'inherit'],
}

/** Text colour of a tone: lighter variants on a dark theme. */
const toneText = ({ theme, $tone = 'gray' }) => TONES[$tone][theme?.crm?.mode === 'dark' ? 2 : 1]

export const Pill = styled.span`
    display: inline-block;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    background: ${({ $tone = 'gray' }) => TONES[$tone][0]};
    color: ${toneText};
`

/** Tone for a contact/deal stage label. */
export const stageTone = stage => {
    if (stage === 'Won' || stage === 'Customer') return 'green'
    if (!stage || stage === 'No deal') return 'gray'
    if (stage === 'Lost') return 'red'
    return 'blue'
}

export const StagePill = ({ stage }) => <Pill $tone={stageTone(stage)}>{stage || 'No deal'}</Pill>

const AvatarBox = styled.span`
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${({ $size }) => Math.round($size * 0.38)}px;
    font-weight: 700;
    background: rgba(59, 130, 246, 0.2);
    color: ${({ theme }) => (theme?.crm?.mode === 'dark' ? '#93c5fd' : '#2563eb')};
    flex-shrink: 0;
`

/** @param {string} name */
export const initials = name =>
    String(name || '?')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')

export const Avatar = ({ name, size = 32 }) => (
    <AvatarBox $size={size} title={name || ''} aria-hidden="true">
        {initials(name)}
    </AvatarBox>
)

/** Filter / saved-view chip. */
export const ChipButton = styled.button`
    border: 1px solid rgba(128, 128, 128, 0.4);
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 0.85rem;
    cursor: pointer;
    background: ${({ $active }) => ($active ? 'rgba(59,130,246,.2)' : 'transparent')};
    color: inherit;
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    &:hover {
        background: rgba(59, 130, 246, 0.12);
    }
`

export const TabBar = styled.div`
    display: flex;
    gap: 4px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.3);
    margin-bottom: 12px;
    overflow-x: auto;
`

export const TabButton = styled.button`
    background: none;
    border: 0;
    border-bottom: 2px solid ${({ $active }) => ($active ? '#2563eb' : 'transparent')};
    color: inherit;
    padding: 8px 12px;
    cursor: pointer;
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    white-space: nowrap;
`

/** Loading / error / empty wrapper so every list handles all three the same way. */
export const AsyncState = ({ loading, error, empty, emptyText = 'Nothing here yet.', onRetry, children, hasData }) => {
    if (error && !hasData) {
        return (
            <Message negative role="alert">
                <Message.Header>{error.message}</Message.Header>
                {onRetry ? (
                    <Button size="mini" onClick={onRetry} style={{ marginTop: 8 }}>
                        Try again
                    </Button>
                ) : null}
            </Message>
        )
    }
    if (loading && !hasData) {
        return (
            <Message icon role="status" aria-live="polite">
                <Icon name="circle notched" loading />
                <Message.Content>Loading…</Message.Content>
            </Message>
        )
    }
    if (empty) {
        return <Message icon="inbox" header={emptyText} role="status" />
    }
    if (error && hasData) {
        // A refresh failed but earlier data is still on screen: say so instead of silently showing stale numbers.
        return (
            <>
                <Message warning role="alert" size="small">
                    <Message.Header>{error.message}</Message.Header>
                    Showing the last data that loaded.
                    {onRetry ? (
                        <Button size="mini" onClick={onRetry} style={{ marginLeft: 8 }}>
                            Try again
                        </Button>
                    ) : null}
                </Message>
                {children}
            </>
        )
    }
    return children
}
